import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { AlertTriangle, Check, Loader2, Smartphone, Wallet } from "lucide-react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import HubtelPaySheet from "@/components/HubtelPaySheet";
import AutoPayToggle from "@/components/pro/AutoPayToggle";
import { API, useAuth } from "@/App";

const fmt = (n) => `GHS ${Number(n || 0).toFixed(2)}`;

/** Slide-up for pros to pay Yorm's service charge from their Yorm wallet or by MoMo. `forced` = can't be closed until paid. */
export default function ServiceChargePaySheet({ open, onOpenChange, token, owed = 0, threshold, forced = false, prefill = "full", autoPay, onPaid }) {
  const { user } = useAuth() || {};
  const [amount, setAmount] = useState("");
  const [method, setMethod] = useState("wallet");
  const [available, setAvailable] = useState(null);
  const [momo, setMomo] = useState(false);
  const [paying, setPaying] = useState(false);
  const [paid, setPaid] = useState(false);
  const [walletDone, setWalletDone] = useState(null);

  const wasOpen = useRef(false);
  const reset = () => {
    setAmount(prefill === "full" && owed > 0 ? Number(owed).toFixed(2) : "");
    setMomo(false); setPaid(false); setWalletDone(null); setPaying(false);
    axios.get(`${API}/pro/wallet?token=${token}`)
      .then((r) => { const a = Number(r.data?.available || 0); setAvailable(a); setMethod(a >= 1 && (owed <= 0 || a + 0.001 >= owed) ? "wallet" : "momo"); })
      .catch(() => { setAvailable(0); setMethod("momo"); });
  };
  useEffect(() => {
    if (!open) { wasOpen.current = false; return; }
    if (wasOpen.current) return; // only reset when the sheet (re)opens, not when `owed` refreshes after a payment
    wasOpen.current = true;
    reset();
  }, [open, owed, prefill, token]); // eslint-disable-line react-hooks/exhaustive-deps
  // Forced sheets stay mounted with open=true, so "Done" has to put the pay form back itself.
  const finish = () => { if (forced) { wasOpen.current = true; reset(); } onOpenChange(false); };

  const value = Math.round(Number(amount || 0) * 100) / 100;
  const walletOk = available != null && available + 0.001 >= value && value >= 1;
  const close = (v) => { if (!v && (!forced || paid)) finish(); };

  const payFromWallet = async () => {
    setPaying(true);
    try {
      const { data } = await axios.post(`${API}/pro/wallet/pay-service-charge?token=${token}`, { amount: value });
      setWalletDone(data); setPaid(true); onPaid?.();
      window.dispatchEvent(new CustomEvent("yorm:wallet-updated", { detail: { type: "service_charge_paid" } }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not pay from your wallet");
    } finally {
      setPaying(false);
    }
  };

  return (
    <>
      <Dialog open={open && !momo} onOpenChange={close}>
        <DialogContent hideCloseButton={forced && !paid} className="max-w-md p-0 gap-0 overflow-hidden" data-testid="service-charge-pay-sheet"
          onInteractOutside={(e) => forced && !paid && e.preventDefault()} onEscapeKeyDown={(e) => forced && !paid && e.preventDefault()}>
          {walletDone ? (
            <div className="px-5 pt-8 pb-6 flex flex-col items-center text-center gap-3" data-testid="service-charge-wallet-done">
              <div className="w-16 h-16 rounded-full bg-[#34C759]/10 flex items-center justify-center"><Check size={32} className="text-[#34C759]" strokeWidth={2.5} /></div>
              <DialogTitle className="text-[22px] font-semibold tracking-[-0.02em] text-gray-900">Paid from your wallet</DialogTitle>
              <DialogDescription className="text-[15px] text-[rgba(60,60,67,0.7)]">
                {fmt(walletDone.amount)} went to Yorm.{walletDone.added_to_credit > 0 ? ` ${fmt(walletDone.added_to_credit)} is now prepaid credit.` : ""}
              </DialogDescription>
              <div className="w-full rounded-2xl bg-white divide-y divide-black/5 text-left mt-1">
                <div className="flex justify-between px-4 py-3 text-[15px]"><span className="text-gray-500">Still owed</span><span className="font-semibold tabular-nums" data-testid="service-charge-wallet-owed-after">{fmt(walletDone.owed)}</span></div>
                <div className="flex justify-between px-4 py-3 text-[15px]"><span className="text-gray-500">Wallet available</span><span className="font-semibold tabular-nums" data-testid="service-charge-wallet-available-after">{fmt(walletDone.available)}</span></div>
              </div>
              <Button className="w-full h-12 rounded-full bg-[#4F988E] hover:bg-[#3d7a72] text-[17px] font-semibold mt-1" onClick={finish} data-testid="service-charge-wallet-close">Done</Button>
            </div>
          ) : (
          <div className="px-5 pt-7 pb-6 space-y-4">
            <div className="text-center">
              <DialogTitle className="text-[13px] font-semibold uppercase tracking-wider text-gray-500">Pay service charge</DialogTitle>
              <DialogDescription className="sr-only">Pay Yorm's service charge from your wallet or with MoMo</DialogDescription>
              <p className="mt-1 text-[34px] font-bold text-gray-900 tabular-nums" data-testid="service-charge-owed">{fmt(owed)}</p>
              <p className="text-[13px] text-gray-500">You owe Yorm</p>
            </div>
            {forced && (
              <div className="rounded-2xl bg-amber-50/80 border border-amber-200 p-4 flex gap-3" data-testid="service-charge-limit-notice">
                <AlertTriangle size={20} className="text-amber-600 shrink-0 mt-0.5" />
                <p className="text-[14px] text-amber-900">You've reached the {fmt(threshold)} limit. Please pay what you owe — from your Yorm wallet or with MoMo — to keep using Yorm.</p>
              </div>
            )}
            <div className="rounded-2xl bg-white p-4 space-y-3">
              <div className="flex gap-2">
                {owed > 0 && <button type="button" onClick={() => setAmount(Number(owed).toFixed(2))} data-testid="service-charge-full"
                  className={`flex-1 h-10 rounded-full text-[14px] font-semibold border transition-colors ${Number(amount) === Number(owed.toFixed(2)) ? "bg-[#4F988E] text-white border-[#4F988E]" : "border-gray-200 text-gray-700"}`}>Full amount</button>}
                <button type="button" onClick={() => setAmount("")} data-testid="service-charge-custom"
                  className="flex-1 h-10 rounded-full text-[14px] font-semibold border border-gray-200 text-gray-700">Other amount</button>
              </div>
              <Input inputMode="decimal" placeholder="Amount (GHS)" value={amount} onChange={(e) => setAmount(e.target.value.replace(/[^\d.]/g, ""))}
                className="h-12 rounded-xl text-[17px]" data-testid="service-charge-amount-input" />
              {value > owed && owed > 0 && <p className="text-[12px] text-gray-500">The extra {fmt(value - owed)} becomes credit for future charges.</p>}
            </div>

            <div className="rounded-2xl bg-white divide-y divide-black/5 overflow-hidden" role="radiogroup" aria-label="Pay with" data-testid="service-charge-methods">
              <button type="button" role="radio" aria-checked={method === "wallet"} onClick={() => setMethod("wallet")} disabled={available != null && available < 1}
                className="w-full flex items-center gap-3 px-4 py-3 text-left disabled:opacity-50 active:bg-black/[0.03] transition-colors" data-testid="service-charge-method-wallet">
                <span className="w-9 h-9 rounded-full bg-[#4F988E]/10 text-[#4F988E] flex items-center justify-center shrink-0"><Wallet size={17} /></span>
                <span className="flex-1 min-w-0">
                  <span className="block text-[15px] font-medium text-gray-900">Yorm wallet</span>
                  <span className="block text-[12px] text-gray-500" data-testid="service-charge-wallet-available">
                    {available == null ? "Checking balance…" : `${fmt(available)} available${value >= 1 && available + 0.001 < value ? " — not enough for this amount" : ""}`}
                  </span>
                </span>
                <span className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${method === "wallet" ? "border-[#4F988E] bg-[#4F988E]" : "border-gray-300"}`}>{method === "wallet" && <Check size={12} className="text-white" strokeWidth={3} />}</span>
              </button>
              <button type="button" role="radio" aria-checked={method === "momo"} onClick={() => setMethod("momo")}
                className="w-full flex items-center gap-3 px-4 py-3 text-left active:bg-black/[0.03] transition-colors" data-testid="service-charge-method-momo">
                <span className="w-9 h-9 rounded-full bg-[#FFCC00]/15 text-[#B8860B] flex items-center justify-center shrink-0"><Smartphone size={17} /></span>
                <span className="flex-1 min-w-0">
                  <span className="block text-[15px] font-medium text-gray-900">Mobile Money</span>
                  <span className="block text-[12px] text-gray-500">Approve the prompt on your phone</span>
                </span>
                <span className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${method === "momo" ? "border-[#4F988E] bg-[#4F988E]" : "border-gray-300"}`}>{method === "momo" && <Check size={12} className="text-white" strokeWidth={3} />}</span>
              </button>
            </div>

            {method === "wallet" ? (
              <>
                {available >= 1 && value > available + 0.001 && (
                  <button type="button" onClick={() => setAmount(Number(available).toFixed(2))} data-testid="service-charge-wallet-use-available"
                    className="w-full text-[14px] font-medium text-[#4F988E] -mt-1">
                    Pay {fmt(available)} from wallet now and the rest with MoMo
                  </button>
                )}
                <Button className="w-full h-12 rounded-full bg-[#4F988E] hover:bg-[#3d7a72] text-[17px] font-semibold" disabled={!walletOk || paying} onClick={payFromWallet} data-testid="service-charge-pay-wallet">
                  {paying ? <Loader2 size={18} className="animate-spin" /> : `Pay ${value >= 1 ? fmt(value) : ""} from wallet`}
                </Button>
              </>
            ) : (
              <Button className="w-full h-12 rounded-full bg-[#4F988E] hover:bg-[#3d7a72] text-[17px] font-semibold" disabled={value < 1} onClick={() => setMomo(true)} data-testid="service-charge-continue">
                Pay {value >= 1 ? fmt(value) : ""} with MoMo
              </Button>
            )}
            {!forced && <button type="button" onClick={() => onOpenChange(false)} className="w-full text-[15px] font-medium text-gray-500" data-testid="service-charge-cancel">Not now</button>}

            {autoPay !== undefined && (
              <AutoPayToggle compact enabled={autoPay} testId="service-charge-sheet-autopay"
                onChanged={(d) => { if (d?.paid_now?.amount) { setWalletDone(d.paid_now); setPaid(true); onPaid?.(); } }} />
            )}
          </div>
          )}
        </DialogContent>
      </Dialog>
      {open && momo && (
        <HubtelPaySheet open initialStep="details" token={token} userPhone={user?.phone}
          onOpenChange={(v) => { if (!v) { setMomo(false); if (paid) finish(); } }}
          target={{ type: "service_charge", id: "me", amount: value, proName: "Yorm", label: "Service charge", payeeNoun: "Yorm", noTip: true }}
          onPaid={() => { setPaid(true); onPaid?.(); }} />
      )}
    </>
  );
}
